import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDict } from "@/lib/i18n/context";
import type { PublicTalentProfile } from "@/lib/types";

type TalentDetail = PublicTalentProfile & { cv_url: string | null };

export function TalentDetailPage() {
  const t = useDict();
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<TalentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`/api/talent/${id}`, { credentials: "include" })
      .then(async (res) => {
        if (res.status === 404) throw new Error("notfound");
        if (!res.ok) throw new Error("fail");
        return res.json() as Promise<{ profile: TalentDetail }>;
      })
      .then((data) => setProfile(data.profile))
      .catch((err) =>
        setError(
          err instanceof Error && err.message === "notfound"
            ? t.talent.notFound
            : t.talent.loadError,
        ),
      )
      .finally(() => setLoading(false));
  }, [id, t.talent.loadError, t.talent.notFound]);

  if (loading) {
    return <p className="text-muted">{t.reports.loading}</p>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/talent"
        className="inline-flex items-center gap-2 text-sm text-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
        {t.talent.back}
      </Link>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {profile && (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <div>
              <h1 className="text-2xl font-bold">
                {profile.full_name || profile.email}
              </h1>
              <p className="mt-1 text-muted">{profile.email}</p>
              <div className="mt-3">
                <Badge tone="brand">{t.jobs.badgePublic}</Badge>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-surface/40 p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
                  <FileText className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-medium">
                    {profile.has_cv ? t.talent.hasCv : t.talent.noCv}
                  </p>
                  {profile.has_cv && profile.cv_url && (
                    <a
                      href={profile.cv_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
                    >
                      {t.profile.download}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <Link
              to={`/candidates/new?email=${encodeURIComponent(profile.email)}&name=${encodeURIComponent(profile.full_name || "")}`}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 sm:w-auto"
            >
              {t.talent.invite}
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
